import { set, get, cloneDeep } from 'lodash-es'
import moment from 'moment'
import { entitySetName } from 'syft-acp-core/reducers/generators/utils'
import { entityFetchBegin } from 'syft-acp-core/reducers/generators/common-reducers/entity-fetch-begin'

const CLOCK_TIME_ATTRS = ['clock_in_time', 'clock_out_time']

const clockValuesAreEqual = (previousValue, nextValue) =>
  previousValue === nextValue ||
  (previousValue && nextValue && moment(previousValue).isSame(moment(nextValue)))

const removeClockTimeChange = (clockTimeChanges, shiftKey, bookingKey, attr) => {
  const shiftChanges = clockTimeChanges[shiftKey]
  const bookingChanges = shiftChanges?.[bookingKey]
  if (!bookingChanges) return

  delete bookingChanges[attr]
  if (!Object.keys(bookingChanges).length) delete shiftChanges[bookingKey]
  if (!Object.keys(shiftChanges).length) delete clockTimeChanges[shiftKey]
}

const setClockTimeChange = (clockTimeChanges, shiftKey, bookingKey, attr, change) => {
  clockTimeChanges[shiftKey] = clockTimeChanges[shiftKey] ?? {}
  clockTimeChanges[shiftKey][bookingKey] = clockTimeChanges[shiftKey][bookingKey] ?? {}
  clockTimeChanges[shiftKey][bookingKey][attr] = change
}

const updateClockTimeChanges = (state, { shiftID, id, attr, val, currentValue }) => {
  const clockTimeChanges = cloneDeep(state.clockTimeChanges ?? {})
  if (!CLOCK_TIME_ATTRS.includes(attr)) return clockTimeChanges

  const shiftKey = String(shiftID)
  const bookingKey = String(id)
  const existingChange = get(clockTimeChanges, [shiftKey, bookingKey, attr])
  const previousValue = existingChange === undefined ? currentValue : existingChange.previousValue

  if (clockValuesAreEqual(previousValue, val)) {
    removeClockTimeChange(clockTimeChanges, shiftKey, bookingKey, attr)
  } else {
    setClockTimeChange(clockTimeChanges, shiftKey, bookingKey, attr, {
      previousValue,
      nextValue: val,
    })
  }

  return clockTimeChanges
}

export const fetchBookingState = state => ({
  ...entityFetchBegin(state),
  clockTimeChanges: {},
})

export const bookingUpdateState = (state, action) => {
  const { shiftID, id, attr, val } = action.payload

  const newState = cloneDeep(state.entityMap[shiftID])
  const newAgencyShifts = newState.agency_shift_bookings.find(item => item.id === id)
  const currentValue = get(newAgencyShifts, attr)
  const clockTimeChanges = updateClockTimeChanges(state, {
    shiftID,
    id,
    attr,
    val,
    currentValue,
  })
  // TODO: The whole underlying logic for (agency and Syft) shifts
  // and bookings should be redone at some point.
  set(newAgencyShifts, attr, val)

  return {
    ...state,
    entityMap: {
      ...state.entityMap,
      [shiftID]: newState,
    },
    clockTimeChanges,
    hasChanges: true,
  }
}

export const failedBookingState = (state, action) => {
  const {
    payload: {
      message,
      response: { status },
      body,
    },
  } = action
  const error_description = get(body, 'error_description') || get(body, 'debug.message')
  const setKey = entitySetName({ shiftID: action.payload.options.shiftID })

  return {
    ...state,
    isSavingData: false,
    lastMessage: String(message),
    lastStatus: String(status),
    lastBody: {
      error_description: String(error_description),
    },
    entitySets: {
      ...state.entitySets,
      [setKey]: {
        ...state.entitySets[setKey],
        isLoadingData: false,
      },
    },
  }
}

const updateAgencyShiftBookings = (state = {}, agencyShiftBookings = []) =>
  state.agency_shift_bookings.map(item => {
    const newItem = agencyShiftBookings.find(responseItem => responseItem.id === item.id)

    return newItem ? newItem : item
  })

export const successSaveBookingState = (state, action) => {
  const { payload: response, request } = action
  const { shiftId } = request

  return {
    ...state,
    entityMap: {
      ...state.entityMap,
      [shiftId]: {
        ...state.entityMap[shiftId],
        agency_shift_bookings: updateAgencyShiftBookings(state.entityMap[shiftId], response),
      },
    },
    entityDetail: {
      ...state.entityDetail,
      [shiftId]: {
        ...state.entityDetail[shiftId],
        agency_shift_bookings: updateAgencyShiftBookings(state.entityDetail[shiftId], response),
      },
    },
    isSavingData: false,
    isLoadingData: false,
    clockTimeChanges: {},
    hasChanges: false,
  }
}
